import { useState, useCallback, useEffect } from "react";
import Axios, { AxiosError } from "axios";
import { PartnerUsersConstants } from "./PartnerUsersConstants";
import toast from "@/components/hoc/SnackbarAlert";
import { Messages } from "@/util/Messages";
import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import Title from "@/components/molecules/Title";
import { Autocomplete } from "@/components/molecules/AutoComplete";
import TextField from "@/components/atoms/TextField";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import {
  PartnerRoles,
  PartnerUser,
  PartnerUserError,
} from "@/models/partnerUsers";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import {
  getPartnerUserAPI,
  updatePartnerUserAPI,
} from "@/util/api/partnerUsers";
import { useParams } from "react-router-dom";
import { getLoginUserAPI } from "@/util/api/API";
import { getAllPartnerRolesAPI } from "@/util/api/partnerRoles";
import { GX_ADMIN } from "@/models/auth";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";
import { ActionButtons } from "@/components/common/buttonGroup/ActionButtons";

export const PartnerUserEdit = () => {
  const params = useParams<{ id: string }>();
  const id = params.id;

  const [user, setUser] = useState<PartnerUser | null>();
  const [userRoles, setUserRoles] = useState<PartnerRoles[]>([]);

  const [loading, updatePartnerUserAPIWrapper] =
    useLoadingPromise(updatePartnerUserAPI);

  const [partnerRolesSelected, setPartnerRolesSelected] =
    useState<PartnerRoles | null>(null);

  const [error, setError] = useState<PartnerUserError>({});
  const { navigate } = useStatefulNavigator();

  // 料金表一覧画面遷移関数
  const goToPartnerUsers = useCallback(() => {
    navigate("/partner_users");
  }, [navigate]);

  const handleChangeUserRole = useCallback(
    (e: React.SyntheticEvent<Element, Event>, value: PartnerRoles | null) => {
      setPartnerRolesSelected(value);
      setUser({
        ...user,
        partnerRoleId: value?.id,
        partnerRoles: value ?? undefined,
      });
      setError({
        partnerRoleId: false,
      });
    },
    [user]
  );

  // ユーザー権限変更
  const handleUpdate = useCallback(() => {
    if (id == null || user == null) {
      return;
    }

    const errorCurrent: PartnerUserError = {
      partnerRoleId: user?.partnerRoleId == null,
    };
    if (Object.values(errorCurrent).some((error) => error)) {
      setError(errorCurrent);
      return;
    }

    updatePartnerUserAPIWrapper(Number(id), user)
      .then(() => goToPartnerUsers())
      .catch((e: AxiosError<ErrorResponse>) => {
        if (
          Axios.isAxiosError(e) &&
          [404, 409].includes(e.response?.status ?? -1)
        ) {
          toast.error(
            `${CommonConstants.Replace}${Messages.ErrorOccurred}: ${e?.response?.data?.message}`
          );
        }
        console.log("===Error occurred while update user===", 3);
      });
  }, [goToPartnerUsers, id, updatePartnerUserAPIWrapper, user]);

  useEffect(() => {
    if (id == null) {
      return;
    }

    getLoginUserAPI()
      .then(({ data }) => {
        getAllPartnerRolesAPI()
          .then((res) =>
            setUserRoles(
              data.role === GX_ADMIN
                ? res.data
                : res.data.filter(({ id }) => id !== GX_ADMIN)
            )
          )
          .catch((e) =>
            console.log("===Error occurred while getting UserRoles===", e)
          );
      })
      .catch((e) =>
        console.log("===Error occurred while getting UserInfo===", e)
      );

    getPartnerUserAPI(Number(id))
      .then(({ data }) => {
        setUser(data);
        setPartnerRolesSelected(data.partnerRoles ?? null);
      })
      .catch((e) =>
        console.log("===Error occurred whiled getting userData===")
      );
  }, [id]);

  return (
    <>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid container spacing={2} sx={{ display: "flex", pl: 2 }}>
          <Title style={{ flexGrow: 1 }}>
            {PartnerUsersConstants.PartnerUserEdit}
          </Title>
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <Table size="small" className="gx-info-table">
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={PartnerUsersConstants.PartnerUserName}
                        value={user?.name ?? ""}
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        multiline
                        sx={{ width: "100%" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={PartnerUsersConstants.OfficeCount}
                        value={
                          user?.partnerRoleId === GX_ADMIN
                            ? PartnerUsersConstants.Hyphen
                            : user?.officeCount ?? ""
                        }
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        multiline
                        sx={{ width: "100%" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <TextField
                        label={PartnerUsersConstants.AvailableOffices}
                        value={
                          user?.partnerRoleId === GX_ADMIN
                            ? PartnerUsersConstants.Hyphen
                            : user?.office?.displayName ?? ""
                        }
                        InputProps={{ readOnly: true, disableUnderline: true }}
                        multiline
                        sx={{ width: "100%" }}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className="required">
                      <Autocomplete
                        label={PartnerUsersConstants.UserRole}
                        options={userRoles}
                        value={partnerRolesSelected}
                        autoComplete
                        autoHighlight
                        getOptionLabel={(opt) => opt?.name ?? ""}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        onChange={handleChangeUserRole}
                        error={!!error.partnerRoleId}
                        errorLabel={`${PartnerUsersConstants.UserRole}${Messages.PleaseEnter}`}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <ActionButtons
                loading={loading}
                onCancel={goToPartnerUsers}
                onSubmit={handleUpdate}
              />
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
